import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  CircularProgress,
  Tooltip,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Autocomplete,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import {
  Close as CloseIcon,
  AutoFixHigh as AutoFixHighIcon,
  Edit as EditIcon,
  Public as PublicIcon,
  Settings as SettingsIcon,
  PlaylistAdd as PlaylistAddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  AccountTree as AccountTreeIcon,
} from "@material-ui/icons";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { tiposApi } from "src/services/tiposApi";
import { subtareasApi } from "src/services/subtareasApi";
import { sucursalesApi } from "src/services/sucursalesApi";
import { empleadosApi } from "src/services/empleadosApi";
import { tareasApi } from "src/services/tareasApi";
import { AlertaModalV2 } from "../AlertaSwall";
import { useSnackbar } from "src/components/snackbar";
import { obtenerMeses } from "src/utils/formatTime";
import { CATEGORIAS } from "src/constants";
import { BRAND } from "src/theme/brandTokens";

// ─── Constantes ──────────────────────────────────────────────────────────────

const MESES = obtenerMeses();

// Arrays vacíos estables: evitan que el default `= []` inline en RTK Query
// cree una referencia nueva en cada render, lo que causaría un bucle infinito
// en useEffect cuando estos valores están en el array de dependencias.
const EMPTY_ARRAY = [];

// ─── Estilos ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    backgroundColor: BRAND.color.primary,
    color: "#fff",
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  closeButton: {
    color: "#fff",
    padding: theme.spacing(0.5),
  },
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  sectionCard: {
    marginBottom: theme.spacing(2.5),
    borderRadius: theme.spacing(1),
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${BRAND.color.primary}`,
  },
  sectionIcon: {
    color: BRAND.color.primary,
    fontSize: "1.1rem",
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  aiCard: {
    background: "linear-gradient(135deg, #fff8ef 0%, #fff3e0 100%)",
    border: `1px solid ${BRAND.color.primary}30`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    boxShadow: "0 1px 4px rgba(255,149,0,0.1)",
  },
  aiHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  aiTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  aiHint: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: 2,
  },
  aiButton: {
    background: `linear-gradient(135deg, ${BRAND.color.primary} 0%, ${BRAND.color.primaryDark} 100%)`,
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    "&:hover": {
      background: `linear-gradient(135deg, ${BRAND.color.primary} 0%, ${BRAND.color.primaryDark} 100%)`,
    },
    "&:disabled": {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  nombrePlanBadge: {
    backgroundColor: `${BRAND.color.primary}18`,
    color: BRAND.color.primary,
    fontWeight: 700,
    fontSize: "0.85rem",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(0.75),
    display: "inline-block",
    marginBottom: theme.spacing(2),
  },
  activeChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  inactiveChip: {
    backgroundColor: "#fafafa",
    color: "#9e9e9e",
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  statusBox: {
    display: "flex",
    alignItems: "center",
    padding: "6px 12px",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    height: 40,
  },
  // Sección tareas
  addTareaRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    marginTop: theme.spacing(2),
  },
  subtareaRow: {
    backgroundColor: theme.palette.grey[50],
  },
  subtareaCell: {
    paddingLeft: theme.spacing(4),
    borderBottom: "none",
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
  subtareaAddRow: {
    backgroundColor: `${BRAND.color.primary}08`,
  },
  expandBtn: {
    padding: 4,
  },
  emptySubtareas: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    fontStyle: "italic",
    paddingLeft: theme.spacing(4),
  },
  sucursalHint: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: theme.spacing(1),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(1),
  },
  cancelButton: {
    textTransform: "none",
    fontWeight: 500,
  },
  saveButton: {
    textTransform: "none",
    fontWeight: 600,
    minWidth: 130,
  },
}));

// ─── Validación ───────────────────────────────────────────────────────────────

const validationSchema = Yup.object().shape({
  valorPlan: Yup.number()
    .typeError("Debe ser un número")
    .min(0, "El valor no puede ser negativo")
    .required("Valor requerido"),
  tipoMoneda: Yup.string().required("Selecciona el tipo de moneda"),
  descripcion: Yup.string().required("Descripción interna requerida"),
});

// ─── Subcomponente: encabezado de sección ─────────────────────────────────────

function SectionHeader({ icon: Icon, title, classes }) {
  return (
    <Box className={classes.sectionHeader}>
      <Icon className={classes.sectionIcon} />
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        {title}
      </Typography>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function EditarPlan({
  plan,
  openEditar,
  setOpenEditar,
  monedas,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [generando, setGenerando] = useState(false);
  const [tareaExpandida, setTareaExpandida] = useState(null);
  const [nuevaTarea, setNuevaTarea] = useState("");
  const [nuevaSubtarea, setNuevaSubtarea] = useState("");
  const [sucursalesSeleccionadas, setSucursalesSeleccionadas] = useState([]);

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: tareasAsociadas = EMPTY_ARRAY, refetch: refetchTareas } =
    tareasApi.useGetTipoPlanQuery(plan?.nombre, {
      skip: !plan?.nombre,
    });
  const { data: todasSucursales = EMPTY_ARRAY } =
    sucursalesApi.useGetSucursalesQuery();
  const { data: planSucursales = EMPTY_ARRAY } =
    tiposApi.useGetTipoPlanesSucursalQuery(plan?.nombre, {
      skip: !plan?.nombre,
    });
  const { data: planSubtareas = EMPTY_ARRAY } =
    subtareasApi.useGetPlanTareaSubtareasQuery();

  // ── Mutations ─────────────────────────────────────────────────────────────
  const [actualizarPlan] = tiposApi.useUpdateTipoPlanMutation();
  const [setSucursalesPlan] = tiposApi.useSetTipoPlanesSucursalMutation();
  const [agregarTareaAPlan] = tiposApi.useAddRelacionTipoPlanTareaMutation();
  const [eliminarTareaAsociadaPlan] =
    tiposApi.useDeleteRelacionTipoPlanTareaMutation();
  const [agregarSubtarea] = subtareasApi.useAddPlanTareaSubtareaMutation();
  const [eliminarSubtarea] = subtareasApi.useDeletePlanTareaSubtareaMutation();
  const [generarDescripcion] = tiposApi.useGenerarDescripcionServicioMutation();

  // Empleados de la sucursal por defecto, para encargado/supervisor.
  const [triggerEmpleados, { data: empleados = EMPTY_ARRAY }] =
    empleadosApi.useLazyGetEmpleadosActivosQuery();

  // ── Inicializar sucursales seleccionadas al abrir ─────────────────────────
  useEffect(() => {
    if (openEditar && planSucursales.length > 0) {
      setSucursalesSeleccionadas(planSucursales.map((s) => s.ref_sucursal));
    } else if (openEditar) {
      setSucursalesSeleccionadas([]);
    }
  }, [openEditar, planSucursales]);

  // ── Formik ────────────────────────────────────────────────────────────────
  // useMemo estabiliza la referencia del objeto: sin esto, enableReinitialize
  // detecta un objeto "nuevo" en cada render y llama resetForm() → bucle infinito.
  const initialValues = useMemo(
    () => ({
      valorPlan: plan?.valor ?? 0,
      tipoMoneda: plan?.tipoMoneda ?? "",
      descripcion: plan?.descripcion ?? "",
      categoria: plan?.categoria ?? "",
      descripcionPublica: plan?.descripcion_publica ?? "",
      alcance: plan?.alcance ?? "",
      condiciones: plan?.condiciones ?? "",
      tipoFrecuenciaDefault: plan?.tipoFrecuencia_default ?? "",
      mesCargaTareasDefault: plan?.mesCargaTareas_default ?? "",
      mesFacturacionDefault: plan?.mesFacturacion_default ?? "",
      sucursalDefault: plan?.sucursal_default ?? "",
      empleadoEncargadoDefault: plan?.empleado_encargado_default ?? "",
      supervisorDefault: plan?.supervisor_default ?? "",
      bloquearAsignacion:
        plan?.bloquear_asignacion !== undefined
          ? Boolean(plan.bloquear_asignacion)
          : false,
      activo: plan?.activo !== undefined ? Boolean(plan.activo) : true,
      orden: plan?.orden ?? 0,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plan?.nombre], // el plan completo cambia solo cuando cambia el registro seleccionado
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const result = await AlertaModalV2({
        title: "¿Guardar cambios?",
        text: `Se actualizará el plan "${plan.nombre}".`,
        confirmButtonText: "Sí, guardar",
        icon: "question",
      }).fire();

      if (!result.isConfirmed) return;

      let valorFinal = values.valorPlan;
      if (String(valorFinal).includes(",")) {
        valorFinal = parseFloat(String(valorFinal).replaceAll(",", "."));
      }

      try {
        await actualizarPlan({
          nombrePlan: plan.nombre,
          valorPlan: valorFinal,
          tipoMoneda: values.tipoMoneda,
          descripcion: values.descripcion,
          categoria: values.categoria || undefined,
          descripcionPublica: values.descripcionPublica || undefined,
          alcance: values.alcance || undefined,
          condiciones: values.condiciones || undefined,
          tipoFrecuenciaDefault: values.tipoFrecuenciaDefault || undefined,
          mesCargaTareasDefault:
            values.tipoFrecuenciaDefault === "AÑO"
              ? values.mesCargaTareasDefault || undefined
              : undefined,
          mesFacturacionDefault:
            values.tipoFrecuenciaDefault === "AÑO"
              ? values.mesFacturacionDefault || undefined
              : undefined,
          sucursalDefault: values.sucursalDefault || undefined,
          empleadoEncargadoDefault:
            values.empleadoEncargadoDefault || undefined,
          supervisorDefault: values.supervisorDefault || undefined,
          bloquearAsignacion: values.bloquearAsignacion ? 1 : 0,
          activo: values.activo ? 1 : 0,
          orden: values.orden,
        }).unwrap();

        await setSucursalesPlan({
          nombre: plan.nombre,
          sucursales: sucursalesSeleccionadas,
        }).unwrap();

        enqueueSnackbar("Plan actualizado correctamente", {
          variant: "success",
        });
        setOpenEditar(false);
      } catch {
        enqueueSnackbar("Error al actualizar el plan. Intenta nuevamente.", {
          variant: "error",
        });
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    isSubmitting,
  } = formik;

  // Carga los empleados de la sucursal por defecto (precargada o elegida).
  useEffect(() => {
    if (values.sucursalDefault) {
      triggerEmpleados({ id: values.sucursalDefault, estado: "ALL" });
    }
  }, [values.sucursalDefault, triggerEmpleados]);

  const empleadoLabel = (emp) =>
    emp ? `${emp.nombre ?? ""} ${emp.apellidos ?? ""}`.trim() : "";

  const empleadoEncargadoSel =
    empleados.find((e) => e.rut === values.empleadoEncargadoDefault) ?? null;
  const supervisorSel =
    empleados.find((e) => e.rut === values.supervisorDefault) ?? null;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCerrar = () => {
    setTareaExpandida(null);
    setNuevaTarea("");
    setNuevaSubtarea("");
    setOpenEditar(false);
  };

  const handleGenerarConIA = async () => {
    setGenerando(true);
    try {
      const resultado = await generarDescripcion({
        nombre: plan.nombre,
        descripcionInterna: values.descripcion || undefined,
        tipoServicio: "PLAN",
      }).unwrap();
      setFieldValue("descripcionPublica", resultado.descripcionPublica);
      setFieldValue("alcance", resultado.alcance);
      setFieldValue("condiciones", resultado.entregable);
      enqueueSnackbar("Descripción generada con IA", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al generar la descripción con IA", {
        variant: "error",
      });
    } finally {
      setGenerando(false);
    }
  };

  const handleAgregarTarea = async () => {
    const nombre = nuevaTarea.trim();
    if (!nombre) return;
    try {
      await agregarTareaAPlan({
        nombrePlan: plan.nombre,
        nombreTarea: nombre.toUpperCase(),
      }).unwrap();
      setNuevaTarea("");
      refetchTareas();
      enqueueSnackbar("Tarea agregada al plan", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al agregar la tarea", { variant: "error" });
    }
  };

  const handleEliminarTarea = async (tarea) => {
    const result = await AlertaModalV2({
      title: "¿Eliminar tarea del plan?",
      text: `Se eliminará "${tarea.nombre}" y todas sus subtareas.`,
      confirmButtonText: "Sí, eliminar",
      icon: "warning",
    }).fire();
    if (!result.isConfirmed) return;
    try {
      await eliminarTareaAsociadaPlan({
        nombrePlan: tarea.refPlanTipo,
        nombre: tarea.nombre,
      }).unwrap();
      if (tareaExpandida === tarea.nombre) setTareaExpandida(null);
      refetchTareas();
      enqueueSnackbar("Tarea eliminada del plan", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al eliminar la tarea", { variant: "error" });
    }
  };

  const handleAgregarSubtarea = async (tarea) => {
    const nombre = nuevaSubtarea.trim();
    if (!nombre) return;
    try {
      await agregarSubtarea({
        refPlanTipo: tarea.refPlanTipo,
        nombreRelacionTareaTipoPlan: tarea.nombre,
        nombre: nombre.toUpperCase(),
      }).unwrap();
      setNuevaSubtarea("");
      enqueueSnackbar("Subtarea agregada", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al agregar la subtarea", { variant: "error" });
    }
  };

  const handleEliminarSubtarea = async (tarea, subtarea) => {
    const result = await AlertaModalV2({
      title: "¿Eliminar subtarea?",
      text: `Se eliminará "${subtarea.nombre}".`,
      confirmButtonText: "Sí, eliminar",
      icon: "warning",
    }).fire();
    if (!result.isConfirmed) return;
    try {
      await eliminarSubtarea({
        refPlanTipo: tarea.refPlanTipo,
        nombreRelacion: tarea.nombre,
        nombre: subtarea.nombre,
      }).unwrap();
      enqueueSnackbar("Subtarea eliminada", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al eliminar la subtarea", { variant: "error" });
    }
  };

  const getSubtareasDeTarea = (tarea) =>
    planSubtareas.filter(
      (s) =>
        s.refPlanTipo === tarea.refPlanTipo &&
        s.nombreRelacionTareaTipoPlan === tarea.nombre,
    );

  const esAnual = values.tipoFrecuenciaDefault === "AÑO";

  if (!plan) return null;

  return (
    <Dialog
      open={openEditar}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      aria-labelledby="editar-plan-title"
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <DialogTitle className={classes.dialogTitle} id="editar-plan-title">
        <Box className={classes.titleContent}>
          <EditIcon />
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Editar Plan de Servicio
          </Typography>
        </Box>
        <IconButton
          className={classes.closeButton}
          onClick={handleCerrar}
          size="small"
          aria-label="Cerrar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogContent className={classes.dialogContent}>
            {/* Identificador del plan */}
            <Box className={classes.nombrePlanBadge}>{plan.nombre}</Box>

            {/* ── Sección 1: Datos del plan ─────────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={EditIcon}
                  title="Datos del plan"
                  classes={classes}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label="Valor *"
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0 }}
                      {...getFieldProps("valorPlan")}
                      error={Boolean(touched.valorPlan && errors.valorPlan)}
                      helperText={touched.valorPlan && errors.valorPlan}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={Boolean(touched.tipoMoneda && errors.tipoMoneda)}
                    >
                      <InputLabel>Moneda *</InputLabel>
                      <Select {...getFieldProps("tipoMoneda")} label="Moneda *">
                        <MenuItem value="">
                          <em>Seleccionar</em>
                        </MenuItem>
                        {monedas.map((m, i) => (
                          <MenuItem key={i} value={m.nombre}>
                            {m.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.tipoMoneda && errors.tipoMoneda && (
                        <Typography
                          variant="caption"
                          color="error"
                          style={{ marginLeft: 14, marginTop: 3 }}
                        >
                          {errors.tipoMoneda}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Categoría</InputLabel>
                      <Select {...getFieldProps("categoria")} label="Categoría">
                        <MenuItem value="">
                          <em>Sin categoría</em>
                        </MenuItem>
                        {CATEGORIAS.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Descripción interna *"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      {...getFieldProps("descripcion")}
                      error={Boolean(touched.descripcion && errors.descripcion)}
                      helperText={
                        (touched.descripcion && errors.descripcion) ||
                        "Visible solo para el equipo interno"
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 2: Información pública + IA ──────────────────────── */}
            <Card className={classes.aiCard}>
              <CardContent>
                <Box className={classes.aiHeader}>
                  <Box className={classes.aiTitleGroup}>
                    <PublicIcon className={classes.sectionIcon} />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        className={classes.sectionTitle}
                      >
                        Información pública del servicio
                      </Typography>
                      <Typography className={classes.aiHint}>
                        Visible para el cliente en propuestas comerciales
                      </Typography>
                    </Box>
                  </Box>
                  <Tooltip
                    title="Genera descripción, alcance y condiciones automáticamente con IA"
                    placement="left"
                  >
                    <span>
                      <Button
                        className={classes.aiButton}
                        onClick={handleGenerarConIA}
                        disabled={generando}
                        startIcon={
                          generando ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <AutoFixHighIcon />
                          )
                        }
                        size="small"
                      >
                        {generando ? "Generando..." : "Generar con IA"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Descripción pública"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="Describe el servicio en términos que el cliente pueda entender..."
                      helperText={`${(values.descripcionPublica || "").length} caracteres`}
                      {...getFieldProps("descripcionPublica")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Alcance"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="¿Qué incluye este servicio?"
                      {...getFieldProps("alcance")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Condiciones"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="Condiciones de entrega o consideraciones importantes..."
                      {...getFieldProps("condiciones")}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 3: Configuración ──────────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={SettingsIcon}
                  title="Configuración"
                  classes={classes}
                />
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Frecuencia por defecto</InputLabel>
                      <Select
                        {...getFieldProps("tipoFrecuenciaDefault")}
                        label="Frecuencia por defecto"
                      >
                        <MenuItem value="">
                          <em>Sin definir</em>
                        </MenuItem>
                        <MenuItem value="MES">Mensual</MenuItem>
                        <MenuItem value="AÑO">Anual</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {esAnual && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Mes carga de tareas</InputLabel>
                          <Select
                            {...getFieldProps("mesCargaTareasDefault")}
                            label="Mes carga de tareas"
                          >
                            <MenuItem value="">
                              <em>Sin definir</em>
                            </MenuItem>
                            {MESES.map((m) => (
                              <MenuItem key={m} value={m}>
                                {m}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Mes facturación</InputLabel>
                          <Select
                            {...getFieldProps("mesFacturacionDefault")}
                            label="Mes facturación"
                          >
                            <MenuItem value="">
                              <em>Sin definir</em>
                            </MenuItem>
                            {MESES.map((m) => (
                              <MenuItem key={m} value={m}>
                                {m}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={esAnual ? 6 : 4}>
                    <TextField
                      label="Orden de visualización"
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0 }}
                      helperText="Menor número = aparece primero"
                      {...getFieldProps("orden")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={esAnual ? 6 : 4}>
                    <Box className={classes.statusBox}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.activo}
                            onChange={(e) =>
                              setFieldValue("activo", e.target.checked)
                            }
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Chip
                            label={values.activo ? "Activo" : "Inactivo"}
                            size="small"
                            className={
                              values.activo
                                ? classes.activeChip
                                : classes.inactiveChip
                            }
                          />
                        }
                        style={{ margin: 0 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 3b: Derivación por defecto ────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={AccountTreeIcon}
                  title="Derivación por defecto (opcional)"
                  classes={classes}
                />
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={todasSucursales}
                      getOptionLabel={(option) => option.nombre ?? option}
                      isOptionEqualToValue={(option, value) =>
                        (option.nombre ?? option) === (value.nombre ?? value)
                      }
                      value={
                        todasSucursales.find(
                          (s) => s.nombre === values.sucursalDefault,
                        ) ?? null
                      }
                      onChange={(_, newValue) => {
                        setFieldValue(
                          "sucursalDefault",
                          newValue ? newValue.nombre : "",
                        );
                        // Reinicia encargado/supervisor al cambiar de sucursal.
                        setFieldValue("empleadoEncargadoDefault", "");
                        setFieldValue("supervisorDefault", "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Sucursal por defecto"
                          placeholder="Seleccionar sucursal..."
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={empleados}
                      disabled={!values.sucursalDefault}
                      getOptionLabel={(option) => empleadoLabel(option)}
                      isOptionEqualToValue={(option, value) =>
                        option.rut === value.rut
                      }
                      value={empleadoEncargadoSel}
                      onChange={(_, newValue) =>
                        setFieldValue(
                          "empleadoEncargadoDefault",
                          newValue ? newValue.rut : "",
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Encargado por defecto"
                          placeholder={
                            values.sucursalDefault
                              ? "Seleccionar encargado..."
                              : "Selecciona primero una sucursal"
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={empleados}
                      disabled={!values.sucursalDefault}
                      getOptionLabel={(option) => empleadoLabel(option)}
                      isOptionEqualToValue={(option, value) =>
                        option.rut === value.rut
                      }
                      value={supervisorSel}
                      onChange={(_, newValue) =>
                        setFieldValue(
                          "supervisorDefault",
                          newValue ? newValue.rut : "",
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Supervisor por defecto"
                          placeholder={
                            values.sucursalDefault
                              ? "Seleccionar supervisor..."
                              : "Selecciona primero una sucursal"
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.bloquearAsignacion}
                          onChange={(e) =>
                            setFieldValue(
                              "bloquearAsignacion",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="small"
                        />
                      }
                      label="Bloquear asignación en la propuesta"
                      style={{ margin: 0 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 4: Tareas del plan ────────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={PlaylistAddIcon}
                  title="Tareas del plan"
                  classes={classes}
                />

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: 40 }} />
                        <TableCell>
                          <strong>Tarea</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Acciones</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tareasAsociadas.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              style={{ padding: "12px 0" }}
                            >
                              Sin tareas asociadas al plan
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      {tareasAsociadas.map((tarea) => {
                        const expanded = tareaExpandida === tarea.nombre;
                        const subtareas = getSubtareasDeTarea(tarea);

                        return (
                          <React.Fragment key={tarea.nombre}>
                            {/* Fila de tarea */}
                            <TableRow>
                              <TableCell style={{ width: 40, paddingRight: 0 }}>
                                <IconButton
                                  className={classes.expandBtn}
                                  size="small"
                                  onClick={() => {
                                    setTareaExpandida(
                                      expanded ? null : tarea.nombre,
                                    );
                                    setNuevaSubtarea("");
                                  }}
                                  title={
                                    expanded
                                      ? "Ocultar subtareas"
                                      : "Ver subtareas"
                                  }
                                >
                                  {expanded ? (
                                    <ExpandLessIcon fontSize="small" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 500 }}
                                >
                                  {tarea.nombre}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {subtareas.length} subtarea
                                  {subtareas.length !== 1 ? "s" : ""}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEliminarTarea(tarea)}
                                  title="Eliminar tarea del plan"
                                  style={{ color: "#d32f2f" }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>

                            {/* Fila expandible: subtareas */}
                            <TableRow key={`${tarea.nombre}-subtareas`}>
                              <TableCell
                                colSpan={3}
                                style={{
                                  paddingTop: 0,
                                  paddingBottom: 0,
                                  border: "none",
                                }}
                              >
                                <Collapse in={expanded} unmountOnExit>
                                  <Box
                                    style={{
                                      paddingLeft: 24,
                                      paddingBottom: 12,
                                      paddingTop: 4,
                                    }}
                                  >
                                    {subtareas.length === 0 ? (
                                      <Typography
                                        className={classes.emptySubtareas}
                                      >
                                        Sin subtareas. Agrega la primera abajo.
                                      </Typography>
                                    ) : (
                                      subtareas.map((st) => (
                                        <Box
                                          key={st.nombre}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="space-between"
                                          style={{
                                            padding: "4px 8px",
                                            marginBottom: 2,
                                            borderRadius: 4,
                                            backgroundColor: "#f5f5f5",
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            style={{ fontSize: "0.8rem" }}
                                          >
                                            • {st.nombre}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleEliminarSubtarea(tarea, st)
                                            }
                                            style={{
                                              color: "#bdbdbd",
                                              padding: 2,
                                            }}
                                          >
                                            <DeleteIcon
                                              style={{ fontSize: "0.9rem" }}
                                            />
                                          </IconButton>
                                        </Box>
                                      ))
                                    )}

                                    {/* Input para agregar subtarea */}
                                    <Box
                                      display="flex"
                                      gap={1}
                                      style={{ marginTop: 8 }}
                                    >
                                      <TextField
                                        size="small"
                                        variant="outlined"
                                        placeholder="Nueva subtarea..."
                                        value={nuevaSubtarea}
                                        onChange={(e) =>
                                          setNuevaSubtarea(e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleAgregarSubtarea(tarea);
                                          }
                                        }}
                                        style={{ flex: 1 }}
                                        inputProps={{
                                          style: { fontSize: "0.85rem" },
                                        }}
                                      />
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={() =>
                                          handleAgregarSubtarea(tarea)
                                        }
                                        disabled={!nuevaSubtarea.trim()}
                                        startIcon={<AddIcon />}
                                        style={{
                                          textTransform: "none",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        Agregar
                                      </Button>
                                    </Box>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Input para agregar nueva tarea al plan */}
                <Box className={classes.addTareaRow}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Nombre de la nueva tarea..."
                    value={nuevaTarea}
                    onChange={(e) => setNuevaTarea(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAgregarTarea();
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAgregarTarea}
                    disabled={!nuevaTarea.trim()}
                    startIcon={<AddIcon />}
                    style={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    Agregar tarea
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* ── Sección 5: Disponibilidad por sucursal ────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={BusinessIcon}
                  title="Disponibilidad por sucursal"
                  classes={classes}
                />
                <Autocomplete
                  multiple
                  options={todasSucursales}
                  getOptionLabel={(option) => option.nombre ?? option}
                  isOptionEqualToValue={(option, value) =>
                    option.nombre === (value.nombre ?? value)
                  }
                  value={todasSucursales.filter((s) =>
                    sucursalesSeleccionadas.includes(s.nombre),
                  )}
                  onChange={(_, newValue) => {
                    setSucursalesSeleccionadas(newValue.map((s) => s.nombre));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.nombre}
                        label={option.nombre}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label="Sucursales habilitadas"
                      placeholder="Seleccionar sucursales..."
                    />
                  )}
                />
                <Typography className={classes.sucursalHint}>
                  Sin selección = disponible en todas las sucursales.
                </Typography>
              </CardContent>
            </Card>
          </DialogContent>

          {/* ── Footer ──────────────────────────────────────────────────────── */}
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={handleCerrar}
              variant="outlined"
              className={classes.cancelButton}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.saveButton}
              disabled={isSubmitting}
              startIcon={
                isSubmitting && <CircularProgress size={16} color="inherit" />
              }
            >
              {isSubmitting ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}
